import { QuestionMark } from '@mui/icons-material';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  LinearProgress,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Box,
  alpha,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { PrintLayout, PrintWidth } from '@/@types/print';
import { PRINT_LAYOUTS, PRINT_WIDTHS } from '@/modules/utils/print-layouts';
import { isAndroidDevice } from '@/modules/utils/print-intent';
import { Modal } from '../Modal';

interface IConfirmPrintModalProps {
  isOpen: boolean;
  isLoading: boolean;
  isConfirmDisabled?: boolean;
  title?: string;
  message?: string;
  cancelButtonText?: string;
  confirmButtonText?: string;
  intentButtonText?: string;
  onClose: () => void;
  onConfirm: () => void;
  // eslint-disable-next-line no-unused-vars
  onIntentConfirm?: () => void;
  selectedLayout: PrintLayout;
  showLayoutSelector?: boolean;
  // eslint-disable-next-line no-unused-vars
  onLayoutChange: (layout: PrintLayout) => void;
  copies: number;
  // eslint-disable-next-line no-unused-vars
  onCopiesChange: (copies: number) => void;
  selectedPrintWidth?: PrintWidth;
  // eslint-disable-next-line no-unused-vars
  onPrintWidthChange?: (width: PrintWidth) => void;
  showPrintWidthSelector?: boolean;
}

export function ConfirmPrintModal({
  isOpen,
  isLoading,
  isConfirmDisabled = false,
  onClose,
  title,
  message = 'Você tem certeza que deseja realizar está ação?',
  cancelButtonText = 'Cancelar',
  confirmButtonText = 'Confirmar',
  intentButtonText = 'Imprimir via Android',
  onConfirm,
  onIntentConfirm,
  selectedLayout,
  showLayoutSelector = true,
  onLayoutChange,
  copies,
  onCopiesChange,
  selectedPrintWidth,
  onPrintWidthChange,
  showPrintWidthSelector = false,
}: IConfirmPrintModalProps) {
  const [isAndroid, setIsAndroid] = useState(false);
  const [copiesInput, setCopiesInput] = useState(String(copies));

  useEffect(() => {
    setIsAndroid(isAndroidDevice());
  }, []);

  useEffect(() => {
    setCopiesInput(String(copies));
  }, [copies]);

  const showIntentButton = Boolean(onIntentConfirm && isAndroid);
  const isCopiesInputEmpty = copiesInput === '';

  const handleCopiesInputChange = (value: string) => {
    setCopiesInput(value);

    if (value === '') return;

    const parsedCopies = parseInt(value, 10);

    if (!Number.isNaN(parsedCopies)) {
      onCopiesChange(Math.min(10, Math.max(1, parsedCopies)));
    }
  };

  return (
    <Modal open={isOpen} onClose={onClose} fullWidth maxWidth="xs">
      {isLoading ? <LinearProgress /> : null}
      <DialogContent sx={{ p: 2 }}>
        <Stack alignItems="center" justifyContent="center">
          <Box
            sx={(theme) => ({
              alignItems: 'center',
              bgcolor: alpha(theme.palette.info.main, 0.16),
              borderRadius: '50%',
              color: theme.palette.info.main,
              display: 'flex',
              height: 48,
              justifyContent: 'center',
              mb: 2,
              width: 48,
            })}
          >
            <QuestionMark color="inherit" fontSize="medium" />
          </Box>
          <Stack sx={{ textAlign: 'center', mb: 3 }}>
            {title ? (
              <DialogContentText variant="h5" fontWeight="bold">
                {title}
              </DialogContentText>
            ) : null}
            <DialogContentText sx={{ textAlign: 'center' }}>
              {message}
            </DialogContentText>
          </Stack>

          <Stack spacing={2} sx={{ width: { xs: '100%', sm: '80%' } }}>
            {showLayoutSelector ? (
              <FormControl fullWidth>
                <InputLabel id="print-layout-label">
                  Layout de Impressão
                </InputLabel>
                <Select
                  labelId="print-layout-label"
                  value={selectedLayout}
                  label="Layout de Impressão"
                  onChange={(e) =>
                    onLayoutChange(e.target.value as PrintLayout)
                  }
                  disabled={isLoading}
                >
                  {PRINT_LAYOUTS.map(({ id, label }) => (
                    <MenuItem key={id} value={id}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : null}
            {showPrintWidthSelector &&
            selectedPrintWidth &&
            onPrintWidthChange ? (
              <FormControl fullWidth>
                <InputLabel id="print-width-label">
                  Largura da bobina
                </InputLabel>
                <Select
                  labelId="print-width-label"
                  value={selectedPrintWidth}
                  label="Largura da bobina"
                  onChange={(event) =>
                    onPrintWidthChange(event.target.value as PrintWidth)
                  }
                  disabled={isLoading}
                >
                  {PRINT_WIDTHS.map(({ id, label }) => (
                    <MenuItem key={id} value={id}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : null}

            <TextField
              type="number"
              label="Número de Cópias"
              value={copiesInput}
              onChange={(e) => handleCopiesInputChange(e.target.value)}
              onBlur={() => {
                if (isCopiesInputEmpty) setCopiesInput(String(copies));
              }}
              inputProps={{ min: 1, max: 10 }}
              disabled={isLoading}
              fullWidth
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          p: 2,
          justifyContent: showIntentButton ? 'stretch' : 'center',
        }}
      >
        {showIntentButton ? (
          <Stack spacing={1} sx={{ width: '100%' }}>
            <Button
              fullWidth
              variant="contained"
              onClick={onIntentConfirm}
              disabled={isLoading || isConfirmDisabled || isCopiesInputEmpty}
            >
              {intentButtonText}
            </Button>
            <Button onClick={onClose} disabled={isLoading} autoFocus>
              {cancelButtonText}
            </Button>
          </Stack>
        ) : (
          <>
            <Button onClick={onClose} disabled={isLoading} autoFocus>
              {cancelButtonText}
            </Button>
            <Button
              variant="contained"
              onClick={onConfirm}
              disabled={isLoading || isConfirmDisabled || isCopiesInputEmpty}
            >
              {confirmButtonText}
            </Button>
          </>
        )}
      </DialogActions>
    </Modal>
  );
}
